import { useState, useMemo } from "react";
import { useDispatch } from "react-redux";
import RequestPageOutlinedIcon from "@mui/icons-material/RequestPageOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import {
  Modal,
  ModalOverflow,
  ModalDialog,
  ModalClose,
  Box,
  Stack,
  Typography,
  Button,
  Checkbox,
  Input,
  Divider,
} from "@financeable-com-au/financeable-ui";
import { ERRORS_MESSAGE, SUCCESS_MESSAGE } from "@/constants";
import { openSnackbar } from "@/store/slices/snackbarSlice";
import { IDispatchResponse } from "@/utils/interface";
// @ts-expect-error: ignoring as it is existing js file
import { sendDocumentRequestLinkToCustomer } from "@/store/slices/applicationFormSlice";
import type { ApplicationType, Customer } from "@/store/slices/types/applicationFormSlice";

const modalWidth = "560px";

interface DocumentItem {
  /** Stable identifier matching the backend schema; custom docs use a generated key */
  key: string;
  name: string;
  description: string;
  /** UI-only flag identifying which documents the broker has selected to request */
  selected: boolean;
}

/** Default documents a broker can request. Editable per-request; not persisted. */
export const DEFAULT_REQUEST_DOCUMENTS: ReadonlyArray<DocumentItem> = [
  { key: "payslips", name: "Payslips", description: "Your most recent payslips", selected: true },
  { key: "second-payslip", name: "2nd payslip", description: "Your second most recent payslip", selected: true },
  { key: "front-of-id", name: "Front of ID", description: "Front of your driver licence or passport", selected: true },
  { key: "back-of-id", name: "Back of ID", description: "Back of your driver licence or photo ID", selected: true },
  { key: "medicare-card", name: "Medicare card", description: "Front of your Medicare card", selected: true },
  { key: "bank-statements", name: "Bank statements", description: "Last 90 days of transactions", selected: true },
  {
    key: "proof-of-address",
    name: "Proof of address",
    description: "Recent utility bill or rates notice",
    selected: false,
  },
  { key: "tax-return-noa", name: "Tax return / NOA", description: "Most recent Notice of Assessment", selected: false },
];

const buildInitialDocuments = (): DocumentItem[] => DEFAULT_REQUEST_DOCUMENTS.map((doc) => ({ ...doc }));

interface RequestDocumentsModalProps {
  open: boolean;
  onClose: () => void;
  customer: Customer;
  applicationId: string;
  applicationType: ApplicationType;
}

const RequestDocumentsModal = ({
  open,
  onClose,
  customer,
  applicationId,
  applicationType,
}: RequestDocumentsModalProps) => {
  const dispatch = useDispatch();
  const [documents, setDocuments] = useState<DocumentItem[]>(buildInitialDocuments);
  const [customLabel, setCustomLabel] = useState("");
  const [isSending, setIsSending] = useState(false);

  const customerName = (customer?.firstName ?? "") + " " + (customer?.lastName ?? "") || "the customer";
  const email = customer?.emails?.slice(-1)[0]?.address;
  const phoneNumber = customer?.phoneNumbers?.slice(-1)[0]?.number;

  const selectedDocuments = useMemo(() => documents.filter((doc) => doc.selected), [documents]);
  const selectedCount = selectedDocuments.length;

  const handle = {
    toggle: (key: string) =>
      setDocuments((prev) => prev.map((doc) => (doc.key === key ? { ...doc, selected: !doc.selected } : doc))),
    editDescription: (key: string, description: string) =>
      setDocuments((prev) => prev.map((doc) => (doc.key === key ? { ...doc, description } : doc))),
    addCustom: () => {
      const name = customLabel.trim();
      if (!name) return;
      setDocuments((prev) => [...prev, { key: `custom-${Date.now()}`, name, description: "", selected: true }]);
      setCustomLabel("");
    },
    closeModal: () => {
      setDocuments(buildInitialDocuments());
      setCustomLabel("");
      onClose?.();
    },
    send: () => {
      if (selectedCount === 0) return;
      setIsSending(true);
      dispatch(
        sendDocumentRequestLinkToCustomer({
          customerId: customer._id,
          applicationId,
          applicationType,
          requestedDocuments: selectedDocuments.map(({ key, name, description }) => ({
            key,
            name,
            description: description.trim(),
          })),
        }),
      ).then(({ error, payload }: IDispatchResponse) => {
        setIsSending(false);
        if (error) {
          dispatch(
            openSnackbar({
              message: payload?.status_message || ERRORS_MESSAGE.fetchErrorMsg,
              intent: "danger",
            }),
          );
        } else {
          dispatch(
            openSnackbar({
              message: payload?.data?.status_message || SUCCESS_MESSAGE.documentRequestLinkSent,
              intent: "success",
            }),
          );
          handle.closeModal();
        }
      });
    },
  };

  return (
    <Modal
      aria-labelledby="request-documents-modal-title"
      aria-describedby="request-documents-modal-desc"
      open={!!open}
      onClose={handle.closeModal}
    >
      <ModalOverflow>
        <ModalDialog minWidth={modalWidth} maxWidth={modalWidth}>
          <ModalClose />
          <Stack rowGap={3}>
            {/* Header */}
            <Stack direction="row" columnGap={2} alignItems="flex-start">
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 40,
                  height: 40,
                  borderRadius: "8px",
                  bgcolor: "primary.softBg",
                  color: "primary.solidBg",
                  flexShrink: 0,
                }}
              >
                <RequestPageOutlinedIcon fontSize="small" />
              </Box>
              <Box>
                <Typography component="h2" id="request-documents-modal-title" level="title-md" bold>
                  Request documents from customer
                </Typography>
                <Typography id="request-documents-modal-desc" level="body-sm" textColor="text.secondary">
                  Choose what <strong>{customerName}</strong> needs to provide. They&rsquo;ll get a secure link to
                  upload everything &mdash; no login required.
                </Typography>
              </Box>
            </Stack>

            {/* Document list */}
            <Stack rowGap={2}>
              <Typography level="body-xs" bold textColor="text.tertiary" style={{ letterSpacing: "0.06em" }}>
                DOCUMENTS TO REQUEST
              </Typography>
              <Stack
                rowGap={2}
                sx={{
                  maxHeight: "42vh",
                  overflowY: "auto",
                  // room so card borders / input focus rings aren't clipped, plus a scrollbar gutter
                  px: 0.25,
                  py: 0.25,
                  pr: 1,
                }}
              >
                {documents.map((doc) => (
                  <Box
                    key={doc.key}
                    sx={{
                      px: 2.25,
                      py: 2,
                      borderRadius: "10px",
                      border: "1px solid",
                      borderColor: doc.selected ? "primary.outlinedBorder" : "neutral.outlinedBorder",
                      bgcolor: doc.selected ? "primary.softBg" : "background.surface",
                      transition: "border-color 0.15s, background-color 0.15s",
                    }}
                  >
                    <Stack direction="row" columnGap={1.75} alignItems="flex-start">
                      {/* Checkbox has no sx; wrap to nudge it into optical alignment with the label */}
                      <Box sx={{ mt: "2px" }}>
                        <Checkbox
                          size="sm"
                          checked={doc.selected}
                          onChange={() => handle.toggle(doc.key)}
                          aria-label={doc.name}
                        />
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography level="title-sm" bold>
                          {doc.name}
                        </Typography>
                        {/* Inline-editable instruction; rendered as a single underline rather than a box.
                            Input has no sx so style it via the Box wrapper. */}
                        <Box
                          sx={{
                            mt: 0.75,
                            color: "text.tertiary",
                            "& .MuiInput-root": {
                              "--Input-paddingInline": "0px",
                              "--Input-minHeight": "1.5rem",
                              "--Input-gap": "0.375rem",
                              "--Input-radius": "0px",
                              "--Input-focusedThickness": "0px",
                              backgroundColor: "transparent",
                              border: "none",
                              borderBottom: "1px solid",
                              borderColor: "transparent",
                              boxShadow: "none",
                              "&::before": { display: "none" },
                              "&:hover": { borderColor: "neutral.outlinedBorder" },
                              "&:focus-within": { borderColor: "primary.outlinedBorder" },
                            },
                            "& .MuiInput-input": { color: "text.secondary" },
                          }}
                        >
                          <Input
                            size="sm"
                            placeholder="Add a short instruction…"
                            value={doc.description}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                              handle.editDescription(doc.key, event.target.value)
                            }
                            startDecorator={<EditOutlinedIcon sx={{ fontSize: "0.9rem" }} />}
                          />
                        </Box>
                      </Box>
                    </Stack>
                  </Box>
                ))}
              </Stack>

              {/* Add custom document */}
              <Stack direction="row" columnGap={1.5} alignItems="center">
                <Box sx={{ flex: 1 }}>
                  <Input
                    size="sm"
                    placeholder="Add another document…"
                    value={customLabel}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => setCustomLabel(event.target.value)}
                    onKeyDown={(event: React.KeyboardEvent) => {
                      if (event.key === "Enter") {
                        event.preventDefault();
                        handle.addCustom();
                      }
                    }}
                  />
                </Box>
                <Button variant="secondary" size="sm" onClick={handle.addCustom} disabled={!customLabel.trim()}>
                  Add
                </Button>
              </Stack>
            </Stack>

            <Divider />

            {/* Delivery target */}
            <Stack
              direction="row"
              columnGap={1.5}
              alignItems="center"
              sx={{ p: 1.5, borderRadius: "8px", bgcolor: "background.level1", color: "text.tertiary" }}
            >
              <MailOutlineIcon sx={{ fontSize: "1.1rem" }} />
              <Box sx={{ minWidth: 0 }}>
                <Typography level="body-sm" textColor="primary.plainColor" noWrap>
                  {email || "No email on file"}
                </Typography>
                {phoneNumber && (
                  <Typography level="body-xs" textColor="text.tertiary">
                    Mobile {phoneNumber} &middot; link also sent by SMS
                  </Typography>
                )}
              </Box>
            </Stack>

            {/* Submit */}
            <Button
              variant="primary"
              size="lg"
              onClick={handle.send}
              disabled={selectedCount === 0 || isSending}
              startDecorator={<SendOutlinedIcon fontSize="small" />}
              style={{ width: "100%" }}
            >
              {isSending
                ? "Sending…"
                : `Send request link · ${selectedCount} ${selectedCount === 1 ? "document" : "documents"}`}
            </Button>
          </Stack>
        </ModalDialog>
      </ModalOverflow>
    </Modal>
  );
};

export default RequestDocumentsModal;
